import { getNestedValue } from "./getNestedValue";
import { joinPathParts, parsePathParts } from "./parsePathParts";
import { setValueAtKey } from "./setValueAtKey";

/**
 * Recursively mutates an object at a specified path by setting a new value.
 *
 * @param obj - The object or array to mutate.
 * @param dataPath - The RFC 6901 JSON Pointer path to the property (e.g., "/a/b/c" or "/items/0/name" for arrays).
 * @param newValue - The value to set at the specified path.
 * @returns A new object or array with the mutation applied.
 */
export const mutateByPath = <T extends Record<string, unknown> | unknown[]>(
  obj: T,
  dataPath: string,
  newValue: unknown,
): T => {
  const { firstPart, restParts } = parsePathParts(dataPath);

  const isArray = Array.isArray(obj);
  const newObj = (isArray ? [...obj] : { ...obj }) as Record<string, unknown> | unknown[];

  if (restParts.length === 0) {
    // Base case: last part, set the value
    setValueAtKey(newObj, firstPart, newValue, isArray);
  } else {
    // Recursive case: get or create the nested value (with copy) and recurse
    const nested = getNestedValue(obj, firstPart, restParts);
    const nextPath = joinPathParts(restParts);
    const recursiveResult = mutateByPath(nested, nextPath, newValue);
    setValueAtKey(newObj, firstPart, recursiveResult, isArray);
  }

  return newObj as T;
};
